import { Injectable } from '@angular/core';
import { FormGroup, AbstractControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { isString } from '../helpers';

import { InvalidFormParam } from './api.interface';
import { isTextErrorResponse, isInvalidFormResponse } from './helpers';
import { FormattedError } from './formatted-error';

const ERRORS_IN_PARAMETERS: string = $localize`:@@system.parameters_error:Ошибка в запросе`;
export const FALLBACK_ERROR_TEXT: string = $localize`:@@system.undefined_error:Неопознанная ошибка`;

@Injectable({
  providedIn: 'root'
})
export class HttpErrorFormater {
  private usedFormControls: string[] = [];

  getFormattedError(event: HttpErrorResponse|null): FormattedError {
    const responseBody: any = event ? event.error : null;

    let html = '';
    let invalidFormParams: InvalidFormParam[] = [];

    if(isTextErrorResponse(responseBody)) {
      html = this.formatTextError(responseBody.data.message);
    } else if(isInvalidFormResponse(responseBody)) {
      invalidFormParams = responseBody.data.invalidParams;
    } else {
      html = this.formatTextError(FALLBACK_ERROR_TEXT);
    }

    return new FormattedError(
      html,
      invalidFormParams,
      (invalidParams: InvalidFormParam[], formGroups: FormGroup[]) => this.formatInvalidFormParams(invalidParams, formGroups)
    );
  }

  formatInvalidFormParams(invalidParams: InvalidFormParam[], formGroups: FormGroup[]): string {
    const nonExistingParameters: InvalidFormParam[] = invalidParams
      .map((invalidParam: InvalidFormParam) => {
        if(this.applyError(invalidParam, formGroups)) {
          return null;
        }

        return invalidParam;
      })
      .map((invalidParam: InvalidFormParam|null) => { // loop again through dotted params
        if(!invalidParam) {
          return null;
        }

        if(!invalidParam.key.includes('.')) {
          return invalidParam;
        }

        if(this.applyError(invalidParam, formGroups, true)) {
          return null;
        }

        return invalidParam;
      })
      .filter((invalidParam: InvalidFormParam|null) => null !== invalidParam) as InvalidFormParam[];

    this.usedFormControls = [];

    if(nonExistingParameters.length > 0) {
      return this.invalidFormParamsToText(nonExistingParameters);
    }
    
    return '';
  }

  private applyError(
    invalidParam: InvalidFormParam,
    formGroups: FormGroup[],
    guessKeyByPath = false
  ): boolean {
    let inputKey: string = invalidParam.key;

    if(guessKeyByPath) {
      inputKey = invalidParam.key
        .split('.')
        .pop() as string;
    }

    const control: AbstractControl|null = this.findUnusedFormControl(formGroups, inputKey);

    if(null !== control) {
      control.setErrors({
        [invalidParam.message]: true
      });

      return true;
    }

    return false;
  }

  private findUnusedFormControl(formGroups: FormGroup[], inputKey: string): AbstractControl|null {
    for(const key in formGroups) {
      const cacheKey = `${ key }_${ inputKey }`;

      if(this.usedFormControls.includes(cacheKey)) {
        continue;
      }

      const control: AbstractControl = formGroups[key].controls[inputKey] || null;
      
      if(null !== control) {
        this.usedFormControls.push(cacheKey);

        return control;
      }
    }

    return null;
  }

  private formatTextError(errors: string[]|string, showTitle = false): string {
    errors = isString(errors) ? [ errors ] : errors;

    let template = '';

    if(showTitle) {
      template += `<div class="hot-toast-content">
        <div class="hot-toast-content__title">${errors.shift()}</div>`;
    }

    if(errors.length > 1 || showTitle) {
      template += '<ul class="hot-toast-content__list">';
      
      errors.forEach(err => {
        template += `<li>${err}</li>`;
      });

      template += '</ul>';
    } else {
      template += errors[0];
    }

    if(showTitle) {
      template += '</div>';
    }

    return template;
  }

  private invalidFormParamsToText(invalidParams: InvalidFormParam[]): string {
    const res: string[] = [ERRORS_IN_PARAMETERS];

    invalidParams.forEach((param: InvalidFormParam) => res.push(`<strong>${param.key}:</strong> ${param.message}`));

    return this.formatTextError(res, true);
  }
}